import React from 'react';
import { X } from 'lucide-react';
import type { User } from '../../../../../types/userMaintenance';
import './UserDetailsModal.css';

interface UserDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onEdit: (user: User) => void;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  isOpen,
  onClose,
  user,
  onEdit
}) => {
  if (!isOpen || !user) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleEditClick = () => {
    onEdit(user);
  };

  const handleEmailClick = (email: string) => {
    window.location.href = `mailto:${email}`;
  };

  return (
    <div className="user-details-modal-backdrop" onClick={handleBackdropClick}>
      <div className="user-details-modal-container">
        {/* Header */}
        <div className="user-details-modal-header">
          <h2 className="user-details-modal-title">User Details</h2>
          <div className="user-details-modal-subtitle">Manage user information</div>
          <button 
            className="user-details-modal-close" 
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="user-details-modal-body">
          {/* User ID and Username Row */}
          <div className="user-details-id-username-row">
            <div className="user-details-field-group">
              <div className="user-details-field-label">USER ID</div>
              <div className="user-details-field-value">{user.id}</div>
            </div>
            <div className="user-details-field-group">
              <div className="user-details-field-label">USERNAME</div>
              <div className="user-details-field-value">{user.username}</div>
            </div>
          </div>

          {/* Status and Permission Row */}
          <div className="user-details-status-permission-row">
            <div className="user-details-status-section">
              <div className="user-details-status-badge">
                <div className="user-details-status-dot"></div>
                <span className="user-details-status-text">Active</span>
              </div>
              <div className="user-details-status-label">Status</div>
            </div>
            <div className="user-details-permission-section">
              <div className="user-details-permission-badge">
                <div className="user-details-permission-dot"></div>
                <span className="user-details-permission-text">{user.permissionLevel || 'Full Access'}</span>
              </div>
              <div className="user-details-permission-label">Permission Level</div>
            </div>
          </div>

          {/* User Information Grid */}
          <div className="user-details-info-grid">
            <div className="user-details-field">
              <div className="user-details-field-label">First Name</div>
              <div className="user-details-field-value">{user.firstName}</div>
            </div>

            <div className="user-details-field">
              <div className="user-details-field-label">Middle Name</div>
              <div className="user-details-field-value">{user.middleName}</div>
            </div>

            <div className="user-details-field">
              <div className="user-details-field-label">Last Name</div>
              <div className="user-details-field-value">{user.lastName}</div>
            </div>

            <div className="user-details-field">
              <div className="user-details-field-label">Security Question</div>
              <div className="user-details-field-value">{user.securityQuestion || 'Not set'}</div>
            </div>

            <div className="user-details-field">
              <div className="user-details-field-label">Email Address</div>
              <div className="user-details-field-value">
                {user.email ? (
                  <button 
                    className="user-details-email-link"
                    onClick={() => handleEmailClick(user.email!)}
                  >
                    {user.email}
                  </button>
                ) : (
                  'Not provided'
                )}
              </div>
            </div>

            <div className="user-details-field">
              <div className="user-details-field-label">Group Name</div>
              <div className="user-details-field-value">{user.groupName || 'Not assigned'}</div>
            </div>

            <div className="user-details-field">
              <div className="user-details-field-label">Access Level</div>
              <div className="user-details-field-value">{user.accessLevel || 'Not set'}</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="user-details-modal-footer">
          <button 
            className="user-details-btn-back"
            onClick={onClose}
          >
            Back to List
          </button>
          <button 
            className="user-details-btn-edit"
            onClick={handleEditClick}
          >
            Edit Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsModal;